/*****components***/
import React, { Component } from "react";
import { Table, DatePicker, Select, Button, Empty, message, Tooltip, Tag, Modal } from "antd";
import moment from "moment";
import { ExcelJS, addSummarySheet, addDataSheet, downloadWorkbook } from "./excelExportUtils";
import { API_CONFIG } from "../../Config/config-env.js";
import axios from "axios";

/*custom components*/
import PageLoading from "../../Components/PageLoading/index.js";

/*functions*/
import { applyExternalDarkMode } from "../../Config/functions.js";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Tiendas Casa & Ideas — mismo catálogo estático que usan los hermanos de esta
// carpeta (index.js / indexNew.js) para el filtro de agencia.
const STORES = [
  { store: "Sucursal La Paz Megacenter", code: "LP01" },
  { store: "Sucursal La Paz Multicine", code: "LP02" },
  { store: "Sucursal Cochabamba", code: "CB01" },
  { store: "Sucursal Santa Cruz Banzer", code: "SC04" },
  { store: "Sucursal Santa Cruz Ventura", code: "SC02" },
  { store: "Sucursal Sucre", code: "SU01" },
];

// Orden fijo de los 8 métodos de cobro, igual en cuadratura y xflow
// del Excel de referencia "Resumen de ventas por medio de cobro".
const METODOS = [
  { key: "EFECTIVO", label: "Efectivo" },
  { key: "QR_ATC", label: "QR ATC" },
  { key: "TARJETA_ATC", label: "Tarjeta ATC" },
  { key: "TARJETA_LINKSER", label: "Tarjeta Linkser" },
  { key: "TODOTIX", label: "Todotix" },
  { key: "QR_BCP", label: "QR BCP" },
  { key: "CONTRAENTREGA", label: "Contraentrega" },
  { key: "LINEA_CREDITO_IISA", label: "Línea de crédito IISA" },
];

// Campos "cierre de caja" que reportArqueo4.js guarda en WFL_EXT_CUADRATURA_MEDIOS_COBRO
// (dotación, tipo de cambio, retiros por método). Los antiguos campos "cuadratura por
// método" (EFECTIVO/QR_ATC/...) se borraron: nadie los escribía (indexCuadraturaV2.js es
// solo-lectura, reportArqueo4.js siempre guardó estos campos "retiro", no esos).
const CIERRE_FIELDS = [
  "DOTACION",
  "TIPO_CAMBIO",
  "CUAD_RET_DOT",
  "CUAD_RET_EFEC",
  "CUAD_RET_USD",
  "RETIRO_TARJ_ATC",
  "RETIRO_TARJ_LINK",
  "CONV_RET_QR_RED_ENLACE",
  "CONV_RET_ISSA",
  "CONV_RET_CONTRAENTREGA",
  "CONV_RET_TODOTIX",
  "CONV_RET_BCP",
];

// Colores de cabecera del Excel de referencia "Resumen de ventas por medio de
// cobro" — mismos valores que los .xcv2-th-* del reporte web (rojo/agencia,
// magenta/cuadratura, verde/xflow, azul/sobrante-faltante).
const XCV2_HEADER_COLORS = {
  red: "FFC00000",
  magenta: "FFD86ECC",
  green: "FF47D45A",
  blue: "FF4E95D9",
};

const emptyCierre = () => {
  const obj = {};
  CIERRE_FIELDS.forEach((f) => {
    obj[f] = 0;
  });
  return obj;
};

// Columnas D-J ("cuadratura declarada") = los retiros por método que reportArqueo4.js
// guardó para ese cierre. El retiro en moneda extranjera (CUAD_RET_USD) se guarda con su
// propio T/C (TIPO_CAMBIO) y acá se convierte y se suma directo al retiro de Efectivo en
// moneda local — es la misma plata física (caja de efectivo), separarla en dos columnas
// solo generaría un desface entre esta columna y "Total SG Xflow" (que no distingue moneda).
// CUAD_RET_DOT (retiro de la dotación inicial) no tiene columna propia en este reporte.
// CONV_RET_ISSA (línea de crédito IISA) sí: es el método LINEA_CREDITO_IISA.
const CUADRATURA_FROM_CIERRE = {
  EFECTIVO: (c) => Number(c.CUAD_RET_EFEC || 0) + Number(c.CUAD_RET_USD || 0) * Number(c.TIPO_CAMBIO || 0),
  QR_ATC: (c) => Number(c.CONV_RET_QR_RED_ENLACE || 0),
  TARJETA_ATC: (c) => Number(c.RETIRO_TARJ_ATC || 0),
  TARJETA_LINKSER: (c) => Number(c.RETIRO_TARJ_LINK || 0),
  TODOTIX: (c) => Number(c.CONV_RET_TODOTIX || 0),
  QR_BCP: (c) => Number(c.CONV_RET_BCP || 0),
  CONTRAENTREGA: (c) => Number(c.CONV_RET_CONTRAENTREGA || 0),
  LINEA_CREDITO_IISA: (c) => Number(c.CONV_RET_ISSA || 0),
};

const cuadraturaMetodos = (row) => {
  const c = row && row.cierre ? row.cierre : emptyCierre();
  const obj = {};
  METODOS.forEach((m) => {
    obj[m.key] = CUADRATURA_FROM_CIERRE[m.key](c);
  });
  return obj;
};

// El backend (utils.processReportResponse) devuelve columnas DATE/DATETIME ya
// formateadas como string "DD/MM/YYYY" (o "DD/MM/YYYY HH:mm"), no ISO. moment(value)
// sin formato explícito las parsea con heurísticas ambiguas (a veces como MM/DD,
// a veces tira warning de "deprecated non-ISO"), produciendo fechas incorrectas y
// claves de fila inconsistentes. Acá se fuerza el formato exacto que manda el backend.
const toDateOnly = (value) => {
  if (!value) return "";
  const str = String(value).trim();
  const m1 = moment(str, "DD/MM/YYYY HH:mm", true);
  if (m1.isValid()) return m1.format("YYYY-MM-DD");
  const m2 = moment(str, "DD/MM/YYYY", true);
  if (m2.isValid()) return m2.format("YYYY-MM-DD");
  // fallback: Date/ISO/lo que sea (ej. si algún día el backend cambia a ISO)
  const m3 = moment(value);
  return m3.isValid() ? m3.format("YYYY-MM-DD") : "";
};

// Clave del registro = FECHA + CAJERO (un cajero cierra caja una sola vez al día, no se
// mueve de tienda en el medio) — NO incluye agencia. La agencia se deriva de los pedidos
// facturados (puede haber más de una si hubo un error operativo; ver NUM_AGENCIAS).
const rowKey = (fecha, cajero) => `${fecha}|${cajero}`;

const emptyMetodos = () => {
  const obj = {};
  METODOS.forEach((m) => {
    obj[m.key] = 0;
  });
  return obj;
};

const sumMetodos = (obj) => METODOS.reduce((acc, m) => acc + Number(obj[m.key] || 0), 0);

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

class indexCuadraturaV2 extends Component {
  state = {
    isLoading: false,
    fromDate: moment().startOf("month"),
    toDate: moment(),
    agenciaFilter: [],
    cajeroFilter: "",
    rows: [],
    hasSearched: false,
    detailModalVisible: false,
    detailLoading: false,
    detailRows: [],
    detailRowInfo: null,
    darkMode: false,
    userOptions: [],
  };

  componentDidMount = () => {
    require("antd/dist/antd.css");
    const darkMode = applyExternalDarkMode(this.props.location.search);
    this.setState({ darkMode });
    this.loadUsers();
  };

  getToken = () => new URLSearchParams(this.props.location.search).get("token");

  // Combo de "Usuario Facturador" — mismo endpoint /users/get-all que ya usan
  // reportArqueo4.js / indexCuadratura.js para listar usuarios de la organización.
  loadUsers = async () => {
    const token = this.getToken();
    try {
      const data = await axios({
        url: API_CONFIG.xflowurl + "/users/get-all",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        method: "post",
      }).then((r) => r.data);

      if (data && data.result && data.result.length > 0) {
        const userOptions = data.result
          .map((u) => ({
            value: u.USERNAME,
            label: [u.USERNAME, [u.FIRSTNAME, u.LASTNAME].filter(Boolean).join(" ")]
              .filter(Boolean)
              .join(" — "),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        this.setState({ userOptions });
      }
    } catch (err) {
      console.log("Error cargando usuarios", err);
    }
  };

  callDashboard = async (code, fromDate, toDate) => {
    const token = this.getToken();
    const resp = await axios({
      url: API_CONFIG.xflowurl + "/components/get-single-by-code",
      data: { component: "dashboards", code },
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      method: "post",
    }).then((r) => r.data);

    const compinfo = resp?.data?.DATA;
    if (!compinfo) return [];

    const components = compinfo.data || [];
    components.forEach((c) => {
      c.header = (c.values && c.values.options) || [];
    });

    const datos = {
      components,
      filterDefinition: [{ name: "FECHA", type: "daterange", value: [fromDate, toDate] }],
      filters: { FECHA: [fromDate, toDate] },
    };

    const report = await axios({
      url: API_CONFIG.xflowurl + "/components/get-component-report",
      data: datos,
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      method: "post",
    }).then((r) => r.data);

    return (report && report.status && report.results && report.results[0]) || [];
  };

  // Popup "ver pedidos" del warning de varias tiendas: trae el detalle crudo de
  // pedidos facturados por ese cajero ese día (componente dashboards
  // RESUMEN_COBRO_XFLOW_DETALLE), para que se pueda revisar cuáles quedaron en
  // qué agencia antes de decidir si es un error operativo.
  openDetailModal = async (row) => {
    this.setState({
      detailModalVisible: true,
      detailLoading: true,
      detailRows: [],
      detailRowInfo: { cajero: row.CAJERO, fecha: row.FECHA },
    });

    const token = this.getToken();
    try {
      const resp = await axios({
        url: API_CONFIG.xflowurl + "/components/get-single-by-code",
        data: { component: "dashboards", code: "RESUMEN_COBRO_XFLOW_DETALLE" },
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        method: "post",
      }).then((r) => r.data);

      const compinfo = resp?.data?.DATA;
      if (!compinfo) {
        this.setState({ detailLoading: false });
        return;
      }
      const components = compinfo.data || [];
      components.forEach((c) => {
        c.header = (c.values && c.values.options) || [];
      });

      const datos = {
        components,
        filterDefinition: [
          { name: "CAJERO", type: "text", value: row.CAJERO },
          { name: "FECHA", type: "date", value: new Date(row.FECHA) },
        ],
        filters: { CAJERO: row.CAJERO, FECHA: new Date(row.FECHA) },
      };

      const report = await axios({
        url: API_CONFIG.xflowurl + "/components/get-component-report",
        data: datos,
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        method: "post",
      }).then((r) => r.data);

      const detailRows = (report && report.status && report.results && report.results[0]) || [];
      this.setState({ detailRows, detailLoading: false });
    } catch (err) {
      console.log("Error cargando detalle de pedidos", err);
      message.error("No se pudo cargar el detalle de pedidos");
      this.setState({ detailLoading: false });
    }
  };

  closeDetailModal = () => {
    this.setState({ detailModalVisible: false, detailRows: [], detailRowInfo: null });
  };

  generateReport = async () => {
    const { fromDate, toDate } = this.state;
    if (!fromDate || !toDate) {
      message.warning("Debe seleccionar el rango de fechas");
      return;
    }

    this.setState({ isLoading: true, hasSearched: true });

    try {
      const from = fromDate.clone().startOf("day").toDate();
      const to = toDate.clone().endOf("day").toDate();

      const [xflowRows, cuadraturaRows] = await Promise.all([
        this.callDashboard("RESUMEN_COBRO_XFLOW", from, to),
        this.callDashboard("RESUMEN_COBRO_CUADRATURA", from, to),
      ]);

      const merged = new Map();

      (xflowRows || []).forEach((r) => {
        const fecha = toDateOnly(r.FECHA);
        const key = rowKey(fecha, r.CAJERO);
        const xflow = {};
        METODOS.forEach((m) => {
          xflow[m.key] = Number(r[m.key] || 0);
        });
        merged.set(key, {
          key,
          AGENCIAS: r.AGENCIAS || "",
          multiAgencia: Number(r.NUM_AGENCIAS || 0) > 1,
          FECHA: fecha,
          CAJERO: r.CAJERO,
          cuadraturaId: null,
          cuadraturaAgencia: "",
          cierre: emptyCierre(),
          xflow,
        });
      });

      // Manda WFL_EXT_RESUMEN_ORDENES (lado xflow): si un cajero/fecha guardó cierre pero
      // no tiene pedidos facturados ese día (o el reporte se generó por error / se corrigió
      // después), esa fila NO se muestra — solo se completa el cierre sobre filas que ya
      // existen por haber facturado algo. Confirmado con el usuario.
      (cuadraturaRows || []).forEach((r) => {
        const fecha = toDateOnly(r.FECHA);
        const key = rowKey(fecha, r.CAJERO);
        const existing = merged.get(key);
        if (!existing) return;
        const cierre = {};
        CIERRE_FIELDS.forEach((f) => {
          cierre[f] = Number(r[f] || 0);
        });
        existing.cuadraturaId = r.ID;
        existing.cuadraturaAgencia = r.AGENCIA || "";
        existing.cierre = cierre;
      });

      let rows = Array.from(merged.values());

      const agenciaFilter = this.state.agenciaFilter;
      if (agenciaFilter && agenciaFilter.length > 0) {
        rows = rows.filter((r) =>
          agenciaFilter.some((store) => String(r.AGENCIAS || "").indexOf(store) >= 0)
        );
      }
      const cajeroFilter = (this.state.cajeroFilter || "").trim().toUpperCase();
      if (cajeroFilter) {
        rows = rows.filter((r) => String(r.CAJERO || "").toUpperCase().indexOf(cajeroFilter) >= 0);
      }

      rows.sort((a, b) => (a.FECHA + a.CAJERO).localeCompare(b.FECHA + b.CAJERO));

      this.setState({ rows, isLoading: false });
    } catch (err) {
      console.log("Error generando reporte", err);
      message.error("Ocurrió un error generando el reporte");
      this.setState({ isLoading: false });
    }
  };

  exportToExcel = async () => {
    const { rows } = this.state;
    if (!rows || rows.length === 0) return;

    const cols = [
      { value: "AGENCIA", title: "Agencia", headerFill: XCV2_HEADER_COLORS.red },
      { value: "CAJERO", title: "Cajero (usuario facturador)", headerFill: XCV2_HEADER_COLORS.red },
      { value: "FECHA", title: "Fecha Factura", headerFill: XCV2_HEADER_COLORS.red },
      ...METODOS.map((m) => ({ value: "cuad_" + m.key, title: m.label, headerFill: XCV2_HEADER_COLORS.magenta })),
      { value: "K_TOTAL", title: "Total Cierre de Usuario", headerFill: XCV2_HEADER_COLORS.magenta },
      ...METODOS.map((m) => ({ value: "xflow_" + m.key, title: m.label, headerFill: XCV2_HEADER_COLORS.green })),
      { value: "S_TOTAL", title: "Total Sg Xflow", headerFill: XCV2_HEADER_COLORS.green },
      { value: "T_SOBRANTE", title: "Sobrante", headerFill: XCV2_HEADER_COLORS.blue },
      { value: "U_FALTANTE", title: "Faltante", headerFill: XCV2_HEADER_COLORS.blue },
    ];

    const exportRows = rows.map((row) => {
      const cuadratura = cuadraturaMetodos(row);
      const k = sumMetodos(cuadratura);
      const s = sumMetodos(row.xflow);
      const diff = s - k;
      const flat = {
        AGENCIA: row.multiAgencia ? row.AGENCIAS + " (VARIAS TIENDAS)" : (row.AGENCIAS || "(sin ventas)"),
        CAJERO: row.CAJERO,
        FECHA: row.FECHA,
        K_TOTAL: Number(k.toFixed(2)),
        S_TOTAL: Number(s.toFixed(2)),
        T_SOBRANTE: diff > 0 ? Number(diff.toFixed(2)) : 0,
        U_FALTANTE: diff < 0 ? Number((-diff).toFixed(2)) : 0,
      };
      METODOS.forEach((m) => {
        flat["cuad_" + m.key] = Number(cuadratura[m.key] || 0);
        flat["xflow_" + m.key] = Number(row.xflow[m.key] || 0);
      });
      return flat;
    });

    const totals = { AGENCIA: "TOTAL", CAJERO: "", FECHA: "" };
    ["K_TOTAL", "S_TOTAL", "T_SOBRANTE", "U_FALTANTE"].forEach((c) => {
      totals[c] = Number(exportRows.reduce((acc, r) => acc + Number(r[c] || 0), 0).toFixed(2));
    });
    METODOS.forEach((m) => {
      totals["cuad_" + m.key] = Number(
        exportRows.reduce((acc, r) => acc + Number(r["cuad_" + m.key] || 0), 0).toFixed(2)
      );
      totals["xflow_" + m.key] = Number(
        exportRows.reduce((acc, r) => acc + Number(r["xflow_" + m.key] || 0), 0).toFixed(2)
      );
    });
    exportRows.push(totals);

    const moneyCols = cols.map((c) => c.value).filter((v) => v !== "AGENCIA" && v !== "CAJERO" && v !== "FECHA");

    const wb = new ExcelJS.Workbook();
    wb.creator = "Reporte Resumen de Ventas por Medio de Cobro";
    wb.created = new Date();

    addSummarySheet(wb, {
      title: "Resumen de ventas por medios de cobro",
      generatedAt: moment().format("DD/MM/YYYY HH:mm"),
      period: `${this.state.fromDate.format("DD/MM/YYYY")}  -  ${this.state.toDate.format("DD/MM/YYYY")}`,
      kpis: [
        ["Filas", rows.length],
        ["Total Cierre de Usuario (Bs.)", totals.K_TOTAL],
        ["Total Sg Xflow (Bs.)", totals.S_TOTAL],
        ["Sobrante (Bs.)", totals.T_SOBRANTE],
        ["Faltante (Bs.)", totals.U_FALTANTE],
      ],
    });

    addDataSheet(wb, { name: "Cuadratura", rows: exportRows, cols, moneyCols, statusKey: null });

    await downloadWorkbook(
      wb,
      "resumen-ventas-medio-cobro." + moment().format("YYYY.MM.DD") + ".xlsx"
    );
  };

  buildColumns = () => {
    const cuadraturaChildren = METODOS.map((m) => ({
      title: m.label,
      key: "cuad_" + m.key,
      width: 110,
      align: "right",
      onHeaderCell: () => ({ className: "xcv2-th-magenta" }),
      render: (_, row) => <span className="xcv2-money">{fmtMoney(cuadraturaMetodos(row)[m.key])}</span>,
    }));

    const xflowChildren = METODOS.map((m) => ({
      title: m.label,
      key: "xflow_" + m.key,
      width: 100,
      align: "right",
      onHeaderCell: () => ({ className: "xcv2-th-green" }),
      render: (_, row) => <span className="xcv2-money">{fmtMoney(row.xflow[m.key])}</span>,
    }));

    const columns = [
      {
        title: "Agencia",
        key: "AGENCIAS",
        width: 240,
        fixed: "left",
        onHeaderCell: () => ({ className: "xcv2-th-red" }),
        render: (_, row) => {
          if (row.multiAgencia) {
            return (
              <Tooltip title={`Este cajero facturó en más de una tienda este día: ${row.AGENCIAS}`}>
                <span>
                  {row.AGENCIAS}{" "}
                  <Tag
                    color="warning"
                    className="xcv2-clickable-tag"
                    onClick={() => this.openDetailModal(row)}
                  >
                    Varias tiendas — ver pedidos
                  </Tag>
                </span>
              </Tooltip>
            );
          }
          if (!row.AGENCIAS) {
            return (
              <Tooltip title="No hay pedidos facturados por xflow este cajero/día — no se pudo detectar la agencia">
                <Tag color="default">Sin ventas / agencia no detectada</Tag>
              </Tooltip>
            );
          }
          return row.AGENCIAS;
        },
      },
      {
        title: "Cajero",
        dataIndex: "CAJERO",
        key: "CAJERO",
        width: 130,
        fixed: "left",
        onHeaderCell: () => ({ className: "xcv2-th-red" }),
      },
      {
        title: "Fecha",
        dataIndex: "FECHA",
        key: "FECHA",
        width: 110,
        fixed: "left",
        onHeaderCell: () => ({ className: "xcv2-th-red" }),
        render: (v) => (v ? moment(v).format("DD/MM/YYYY") : ""),
      },
      {
        title: "Cuadratura (cierre de caja)",
        onHeaderCell: () => ({ className: "xcv2-th-magenta" }),
        children: [
          ...cuadraturaChildren,
          {
            title: "Total Cierre de Usuario",
            key: "K_TOTAL",
            width: 130,
            align: "right",
            onHeaderCell: () => ({ className: "xcv2-th-magenta" }),
            render: (_, row) => <b className="xcv2-money">{fmtMoney(sumMetodos(cuadraturaMetodos(row)))}</b>,
          },
        ],
      },
      {
        title: "Sistema de Facturación Xflow",
        onHeaderCell: () => ({ className: "xcv2-th-green" }),
        children: [
          ...xflowChildren,
          {
            title: "Total Sg Xflow",
            key: "S_TOTAL",
            width: 120,
            align: "right",
            onHeaderCell: () => ({ className: "xcv2-th-green" }),
            render: (_, row) => <b className="xcv2-money">{fmtMoney(sumMetodos(row.xflow))}</b>,
          },
        ],
      },
      {
        title: "Sobrante",
        key: "T_SOBRANTE",
        width: 100,
        align: "right",
        onHeaderCell: () => ({ className: "xcv2-th-blue" }),
        render: (_, row) => {
          const diff = sumMetodos(row.xflow) - sumMetodos(cuadraturaMetodos(row));
          return <span style={{ color: diff > 0 ? "#389e0d" : undefined }}>{fmtMoney(diff > 0 ? diff : 0)}</span>;
        },
      },
      {
        title: "Faltante",
        key: "U_FALTANTE",
        width: 100,
        align: "right",
        onHeaderCell: () => ({ className: "xcv2-th-blue" }),
        render: (_, row) => {
          const diff = sumMetodos(row.xflow) - sumMetodos(cuadraturaMetodos(row));
          return <span style={{ color: diff < 0 ? "#cf1322" : undefined }}>{fmtMoney(diff < 0 ? -diff : 0)}</span>;
        },
      },
    ];

    return columns;
  };

  render() {
    const { rows, isLoading, hasSearched } = this.state;

    return (
      <div className="xcv2">
        <PageLoading isLoading={isLoading} />
        <style>{PAGE_CSS}</style>

        <div className="xcv2-header">
          <div className="xcv2-title">
            <h2>Resumen de Ventas por Medio de Cobro</h2>
            <small>Cuadratura de caja vs facturación Xflow, por fecha / cajero</small>
          </div>
        </div>

        <div className="xcv2-panel">
          <div className="xcv2-filters-grid">
            <div className="xcv2-field">
              <label>Rango de Fechas (Fecha Factura)</label>
              <RangePicker
                format="DD/MM/YYYY"
                value={[this.state.fromDate, this.state.toDate]}
                onChange={(values) => {
                  if (values && values.length === 2) {
                    this.setState({ fromDate: values[0], toDate: values[1] });
                  }
                }}
                style={{ width: "100%" }}
                dropdownClassName="xcv2-popup"
              />
            </div>
            <div className="xcv2-field">
              <label>Agencia</label>
              <Select
                mode="multiple"
                allowClear
                placeholder="Todas"
                style={{ width: "100%" }}
                value={this.state.agenciaFilter}
                onChange={(v) => this.setState({ agenciaFilter: v })}
                dropdownClassName="xcv2-popup"
              >
                {STORES.map((s) => (
                  <Option key={s.store} value={s.store}>
                    {s.store}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="xcv2-field">
              <label>Usuario Facturador</label>
              <Select
                showSearch
                allowClear
                placeholder="Todos"
                style={{ width: "100%" }}
                value={this.state.cajeroFilter || undefined}
                onChange={(v) => this.setState({ cajeroFilter: v || "" })}
                options={this.state.userOptions}
                filterOption={(input, option) =>
                  (option?.label || "").toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                dropdownClassName="xcv2-popup"
              />
            </div>
          </div>
          <div className="xcv2-filters-footer">
            <Button type="primary" onClick={this.generateReport}>
              Generar Reporte
            </Button>
            <Button onClick={this.exportToExcel} disabled={!rows.length}>
              Exportar a Excel
            </Button>
          </div>
        </div>

        <div className="xcv2-tables">
          {hasSearched && rows.length === 0 && !isLoading ? (
            <Empty description="No se encontraron datos para el periodo seleccionado" />
          ) : (
            <Table
              rowKey="key"
              columns={this.buildColumns()}
              dataSource={rows}
              size="small"
              scroll={{ x: 1950 }}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              bordered
            />
          )}
        </div>

        <Modal
          title={
            this.state.detailRowInfo
              ? `Pedidos facturados — ${this.state.detailRowInfo.cajero} — ${moment(
                this.state.detailRowInfo.fecha
              ).format("DD/MM/YYYY")}`
              : "Pedidos facturados"
          }
          open={this.state.detailModalVisible}
          onCancel={this.closeDetailModal}
          footer={<Button onClick={this.closeDetailModal}>Cerrar</Button>}
          width={760}
          wrapClassName={this.state.darkMode ? "xflow-external-dark" : ""}
          bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
        >
          <Table
            rowKey="ID_PEDIDO"
            size="small"
            loading={this.state.detailLoading}
            dataSource={this.state.detailRows}
            pagination={false}
            columns={[
              { title: "Pedido", dataIndex: "ID_PEDIDO", key: "ID_PEDIDO", width: 90 },
              { title: "Agencia", dataIndex: "AGENCIA", key: "AGENCIA" },
              { title: "Método de Pago", dataIndex: "METODO_PAGO", key: "METODO_PAGO" },
              {
                title: "Monto",
                dataIndex: "MONTO",
                key: "MONTO",
                align: "right",
                render: (v) => fmtMoney(v),
              },
              { title: "Método de Pago 2", dataIndex: "METODODEPAGO2", key: "METODODEPAGO2" },
              {
                title: "Monto 2",
                dataIndex: "MONTO2",
                key: "MONTO2",
                align: "right",
                render: (v) => fmtMoney(v),
              },
              { title: "Nro. Factura", dataIndex: "NROFACTURA", key: "NROFACTURA" },
            ]}
          />
        </Modal>
      </div>
    );
  }
}

const PAGE_CSS = `
.xcv2 { font-size: 13px; }
.xcv2 .xcv2-header { display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:12px; margin-bottom:14px; }
.xcv2 .xcv2-title h2 { margin:0; font-weight:700; letter-spacing:-0.3px; }
.xcv2 .xcv2-title small { color:#8c8c8c; }
.xcv2 .xcv2-panel { background:#fff; border:1px solid #ececec; border-radius:10px; padding:14px 16px; margin-bottom:14px; box-shadow:0 1px 3px rgba(15,23,42,.05); }
.xcv2 .xcv2-filters-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); gap:10px 12px; }
.xcv2 .xcv2-field label { display:block; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.4px; color:#8c8c8c; margin-bottom:3px; }
.xcv2 .xcv2-filters-footer { display:flex; justify-content:flex-start; gap:8px; align-items:center; margin-top:12px; flex-wrap:wrap; }
.xcv2 .xcv2-tables { background:#fff; border:1px solid #ececec; border-radius:10px; padding:6px 16px 16px; box-shadow:0 1px 3px rgba(15,23,42,.05); }
.xcv2 .ant-table-thead > tr > th { background:#fafafa; font-size:11px; text-transform:uppercase; letter-spacing:.3px; color:#595959; white-space:nowrap; }
/* Colores de cabecera del Excel base "Resumen de ventas por medio de cobro" */
.xcv2 .ant-table-thead > tr > th.xcv2-th-red { background:#C00000 !important; color:#fff !important; }
.xcv2 .ant-table-thead > tr > th.xcv2-th-magenta { background:#D86ECC !important; color:#fff !important; }
.xcv2 .ant-table-thead > tr > th.xcv2-th-green { background:#47D45A !important; color:#fff !important; }
.xcv2 .ant-table-thead > tr > th.xcv2-th-blue { background:#4E95D9 !important; color:#fff !important; }
.xcv2 .ant-table-tbody > tr > td { font-size:12.5px; }
.xcv2 .xcv2-money { font-variant-numeric:tabular-nums; }
.xcv2 .xcv2-clickable-tag { cursor:pointer; }

/* ---- Modo noche: todo lo que vive DENTRO de .xcv2 en el DOM ---- */
.xflow-external-dark .xcv2 { color:#e2e8f0; }
.xflow-external-dark .xcv2 .xcv2-panel,
.xflow-external-dark .xcv2 .xcv2-tables { background:#1f2430 !important; border-color:#2c3242 !important; }
.xflow-external-dark .xcv2 .xcv2-title small { color:#8592ad; }
.xflow-external-dark .xcv2 .xcv2-field label { color:#8592ad; }

.xflow-external-dark .xcv2 .ant-table { background:transparent !important; color:#e2e8f0; }
.xflow-external-dark .xcv2 .ant-table-thead > tr > th { background:#252b39 !important; color:#aab !important; border-color:#2c3242 !important; }
.xflow-external-dark .xcv2 .ant-table-thead > tr > th.xcv2-th-red { background:#C00000 !important; color:#fff !important; }
.xflow-external-dark .xcv2 .ant-table-thead > tr > th.xcv2-th-magenta { background:#D86ECC !important; color:#fff !important; }
.xflow-external-dark .xcv2 .ant-table-thead > tr > th.xcv2-th-green { background:#47D45A !important; color:#fff !important; }
.xflow-external-dark .xcv2 .ant-table-thead > tr > th.xcv2-th-blue { background:#4E95D9 !important; color:#fff !important; }
.xflow-external-dark .xcv2 .ant-table-tbody > tr > td { background:#1a1f2b !important; color:#e2e8f0 !important; border-color:#2c3242 !important; }
.xflow-external-dark .xcv2 .ant-table-tbody > tr:hover > td { background:#232a3a !important; }
.xflow-external-dark .xcv2 .ant-table.ant-table-bordered .ant-table-container,
.xflow-external-dark .xcv2 .ant-table-cell-fix-left,
.xflow-external-dark .xcv2 .ant-table-cell-fix-right { background:#1a1f2b !important; border-color:#2c3242 !important; }
.xflow-external-dark .xcv2 .ant-table-placeholder td,
.xflow-external-dark .xcv2 .ant-table-placeholder:hover td { background:#1a1f2b !important; }
.xflow-external-dark .xcv2 .ant-table-container { border-color:#2c3242 !important; }
.xflow-external-dark .xcv2 .ant-table-body,
.xflow-external-dark .xcv2 .ant-table-content,
.xflow-external-dark .xcv2 .ant-table-sticky-holder,
.xflow-external-dark .xcv2 .ant-table-sticky-scroll { background:#1a1f2b !important; }
.xflow-external-dark .xcv2 .ant-table-sticky-scroll-bar { background:#475569 !important; }
.xflow-external-dark .xcv2 .ant-table-sticky-scroll-bar:hover { background:#64748b !important; }
/* scrollbar nativo (webkit) del contenedor con scroll horizontal/vertical */
.xflow-external-dark .xcv2 .ant-table-body::-webkit-scrollbar,
.xflow-external-dark .xcv2 .ant-table-content::-webkit-scrollbar { height:10px; width:10px; background:#1a1f2b; }
.xflow-external-dark .xcv2 .ant-table-body::-webkit-scrollbar-track,
.xflow-external-dark .xcv2 .ant-table-content::-webkit-scrollbar-track { background:#1a1f2b; }
.xflow-external-dark .xcv2 .ant-table-body::-webkit-scrollbar-thumb,
.xflow-external-dark .xcv2 .ant-table-content::-webkit-scrollbar-thumb { background:#475569; border-radius:6px; }
.xflow-external-dark .xcv2 .ant-empty-description { color:#8592ad; }
.xflow-external-dark .xcv2 .ant-empty-img-simple-ellipse { fill:#334155; }
.xflow-external-dark .xcv2 .ant-empty-img-simple-g { stroke:#475569; }
.xflow-external-dark .xcv2 .ant-empty-img-simple-path { fill:#1f2430; }

.xflow-external-dark .xcv2 .ant-picker,
.xflow-external-dark .xcv2 .ant-select:not(.ant-select-disabled) .ant-select-selector,
.xflow-external-dark .xcv2 .ant-input {
  background:#161b26 !important; border-color:#334155 !important; color:#e2e8f0 !important;
}
.xflow-external-dark .xcv2 .ant-picker-input > input,
.xflow-external-dark .xcv2 .ant-picker-range-separator,
.xflow-external-dark .xcv2 .ant-select-selection-item,
.xflow-external-dark .xcv2 .ant-select-selection-search-input,
.xflow-external-dark .xcv2 .ant-input { color:#e2e8f0; }
.xflow-external-dark .xcv2 .ant-select-selection-placeholder,
.xflow-external-dark .xcv2 .ant-picker-input > input::placeholder,
.xflow-external-dark .xcv2 .ant-input::placeholder,
.xflow-external-dark .xcv2 .ant-picker-separator { color:#64748b; }
.xflow-external-dark .xcv2 .ant-picker-suffix,
.xflow-external-dark .xcv2 .ant-picker-clear,
.xflow-external-dark .xcv2 .ant-select-arrow,
.xflow-external-dark .xcv2 .ant-select-clear,
.xflow-external-dark .xcv2 .ant-input-clear-icon { color:#64748b; background:#161b26; }
.xflow-external-dark .xcv2 .ant-picker:hover,
.xflow-external-dark .xcv2 .ant-select:hover .ant-select-selector,
.xflow-external-dark .xcv2 .ant-input:hover { border-color:#475569; }
.xflow-external-dark .xcv2 .ant-picker-focused,
.xflow-external-dark .xcv2 .ant-select-focused .ant-select-selector,
.xflow-external-dark .xcv2 .ant-input:focus,
.xflow-external-dark .xcv2 .ant-input-focused { border-color:#1890ff; box-shadow:0 0 0 2px rgba(24,144,255,.2); }
.xflow-external-dark .xcv2 .ant-select-multiple .ant-select-selection-item { background:#233047; border-color:#334155; color:#dbeafe; }
.xflow-external-dark .xcv2 .ant-select-multiple .ant-select-selection-item-remove { color:#8592ad; }

.xflow-external-dark .xcv2 .ant-btn:not(.ant-btn-primary):not(.ant-btn-link) {
  background:#1f2430; border-color:#334155; color:#e2e8f0;
}
.xflow-external-dark .xcv2 .ant-btn:not(.ant-btn-primary):not(.ant-btn-link):hover,
.xflow-external-dark .xcv2 .ant-btn:not(.ant-btn-primary):not(.ant-btn-link):focus {
  background:#232a3a; border-color:#475569; color:#fff;
}
.xflow-external-dark .xcv2 .ant-btn[disabled],
.xflow-external-dark .xcv2 .ant-btn[disabled]:hover { background:#181c26; border-color:#2c3242; color:#4b5563; }

.xflow-external-dark .xcv2 .ant-tag-warning { background:#3a2e08; border-color:#faad14; color:#ffe58f; }

.xflow-external-dark .xcv2 .ant-pagination { background:transparent; }
.xflow-external-dark .xcv2 .ant-pagination-item { background:#1f2430 !important; border-color:#334155 !important; }
.xflow-external-dark .xcv2 .ant-pagination-item a { color:#e2e8f0 !important; }
.xflow-external-dark .xcv2 .ant-pagination-item-active { background:#1890ff !important; border-color:#1890ff !important; }
.xflow-external-dark .xcv2 .ant-pagination-item-active a { color:#fff !important; }
.xflow-external-dark .xcv2 .ant-pagination-total-text,
.xflow-external-dark .xcv2 .ant-pagination-options-quick-jumper { color:#8592ad; }
.xflow-external-dark .xcv2 .ant-pagination-prev .ant-pagination-item-link,
.xflow-external-dark .xcv2 .ant-pagination-next .ant-pagination-item-link { background:#1f2430; border-color:#334155; color:#e2e8f0; }
.xflow-external-dark .xcv2 .ant-select-selector .ant-select-selection-search-input,
.xflow-external-dark .xcv2 .ant-pagination-options-quick-jumper input { background:#161b26; border-color:#334155; color:#e2e8f0; }

/* ---- Modo noche: elementos que hacen portal a document.body (dropdowns, picker
   panel, modal, tooltip) — NO están dentro de .xcv2 en el DOM, así que no sirven
   los selectores de arriba. Se activan igual con .xflow-external-dark en <body>. ---- */
.xflow-external-dark .xcv2-popup.ant-select-dropdown,
.xflow-external-dark .xcv2-popup.ant-picker-dropdown .ant-picker-panel-container {
  background:#182235; border:1px solid #334155; box-shadow:0 12px 32px rgba(0,0,0,.5);
}
.xflow-external-dark .xcv2-popup .ant-select-item { color:#e2e8f0; }
.xflow-external-dark .xcv2-popup .ant-select-item-option-active:not(.ant-select-item-option-disabled) { background:#232a3a; }
.xflow-external-dark .xcv2-popup .ant-select-item-option-selected:not(.ant-select-item-option-disabled) { background:#233047; color:#93c5fd; }
.xflow-external-dark .xcv2-popup .ant-select-item-empty { color:#64748b; }
.xflow-external-dark .xcv2-popup .ant-picker-panel { background:#182235; border-color:#334155; }
.xflow-external-dark .xcv2-popup .ant-picker-header { color:#e2e8f0; border-color:#334155; }
.xflow-external-dark .xcv2-popup .ant-picker-header button { color:#8592ad; }
.xflow-external-dark .xcv2-popup .ant-picker-header-view button:hover { color:#e2e8f0; }
.xflow-external-dark .xcv2-popup .ant-picker-content th { color:#8592ad; }
.xflow-external-dark .xcv2-popup .ant-picker-cell { color:#475569; }
.xflow-external-dark .xcv2-popup .ant-picker-cell-in-view { color:#e2e8f0; }
.xflow-external-dark .xcv2-popup .ant-picker-cell:hover .ant-picker-cell-inner { background:#232a3a; }
.xflow-external-dark .xcv2-popup .ant-picker-cell-selected .ant-picker-cell-inner,
.xflow-external-dark .xcv2-popup .ant-picker-cell-range-start .ant-picker-cell-inner,
.xflow-external-dark .xcv2-popup .ant-picker-cell-range-end .ant-picker-cell-inner { background:#1890ff !important; color:#fff; }
.xflow-external-dark .xcv2-popup .ant-picker-cell-in-range::before { background:#1a2c42; }
.xflow-external-dark .xcv2-popup .ant-picker-cell-today .ant-picker-cell-inner::before { border-color:#1890ff; }
.xflow-external-dark .xcv2-popup .ant-picker-footer { border-color:#334155; }
.xflow-external-dark .xcv2-popup .ant-picker-today-btn { color:#60a5fa; }
.xflow-external-dark .xcv2-popup .ant-picker-panel-container .ant-picker-panel { border-color:#334155; }

.xflow-external-dark .ant-tooltip-inner { background:#182235; color:#e2e8f0; }
.xflow-external-dark .ant-tooltip-arrow::before { background:#182235; }

/* El Modal "Pedidos facturados" se marca con wrapClassName=xflow-external-dark
   cuando el modo noche está activo (ver openDetailModal / render). */
.xflow-external-dark .ant-modal-content,
.xflow-external-dark .ant-modal-header { background:#1f2430; }
.xflow-external-dark .ant-modal-title,
.xflow-external-dark .ant-modal-close { color:#e2e8f0; }
.xflow-external-dark .ant-modal-close:hover { color:#fff; }
.xflow-external-dark .ant-modal-body { color:#e2e8f0; }
.xflow-external-dark .ant-modal-header { border-color:#2c3242; }
.xflow-external-dark .ant-modal .ant-table { background:transparent; color:#e2e8f0; }
.xflow-external-dark .ant-modal .ant-table-thead > tr > th { background:#252b39; color:#aab; border-color:#2c3242; }
.xflow-external-dark .ant-modal .ant-table-tbody > tr > td { background:#1a1f2b; color:#e2e8f0; border-color:#2c3242; }
.xflow-external-dark .ant-modal .ant-table-placeholder td { background:#1a1f2b; }
.xflow-external-dark .ant-modal .ant-btn:not(.ant-btn-primary) { background:#1f2430; border-color:#334155; color:#e2e8f0; }
`;

export default indexCuadraturaV2;
